import React, { useState, useEffect, useCallback } from 'react';
import { Solicitud } from '@/types/solicitud';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { solicitudService } from '@/lib/api/solicitudService';
import { empresaService } from '@/lib/api/empresaService';
import { User } from '@/types/user';
import { Empresa } from '@/types/empresa';
import { getAsesoresCached, getEmpresasCached, getAllUsersCached } from '@/hooks/useSharedData';
import { ModuloVista } from '@/utils/fieldVisibility';
import { getCurrentUser } from '@/services/authService';
import { RevisionDocumentalSection, REVISION_DOCUMENTAL_FIELDS, getRevisionDocumentalValue } from './RevisionDocumentalSection';
import { PreAnalisisSection } from './PreAnalisisSection';
import { AnalisisCreditosSection } from './AnalisisCreditosSection';
import { SolicitudControlFechas } from './SolicitudControlFechas';
import { SolicitarVoBoSection } from './SolicitarVoBoSection';
import { AutorizacionRiesgosSection } from './AutorizacionRiesgosSection';
import { CobranzaSection } from './CobranzaSection';
import { ClabesSection } from './ClabesSection';

const SWITCH_LABELS: Record<string, string> = {
  fechaContacto: 'Solicitud',
  fechaEntregaARiesgos: 'Entrega a Riesgos',
  fechaRevisionDocumentalConluida: 'Revisión Documental Concluída',
  fechaAnalisisConluido: 'Análisis Concluído',
  fechaVoBo: 'VoBo',
  fechaPropuesta: 'Autorizado Riesgos',
  fechaRechazoRiesgos: 'Rechazo Riesgos',
  fechaAutorizacionDG: 'Autorización DG',
  fechaRechazoDG: 'Rechazo DG',
  fechaContratoImpreso: 'Contrato Impreso',
  fechaEntregaContratoFirmado: 'Firmar Contrato',
  fechaFondeado: 'Fondeado',
  fechaCancelacionCliente: 'Cancelado por Cliente',
  fechaCancelacionCartera: 'Cancelado por Cartera',
  fechaPrimerSolicitudVoBo: '1ra Solicitud VoBo',
  fechaSegundaSolicitudVoBo: '2da Solicitud VoBo',
  fechaTercerSolicitudVoBo: '3ra Solicitud VoBo',
  fechaValidacionClabe: 'Validación CLABE',
  fechaReferenciaCobranza: 'Referencia Cobranza',
};

interface SolicitudDetailProps {
  isOpen: boolean;
  onClose: () => void;
  solicitud: Solicitud | null;
  onUpdate: (solicitud: Solicitud) => void;
  modulo?: ModuloVista; // Define qué campos mostrar
  readOnly?: boolean; // Modo solo lectura (ver sin editar)
}

export const SolicitudDetail: React.FC<SolicitudDetailProps> = ({ isOpen, onClose, solicitud, onUpdate, modulo = 'all', readOnly = false }) => {
  const [activeTab, setActiveTab] = useState('switches'); // Switches primero, como le gusta al cliente
  const [updatingField, setUpdatingField] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const errorRef = React.useRef<HTMLDivElement>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingRevision, setIsSavingRevision] = useState(false);
  const [localSolicitud, setLocalSolicitud] = useState<Solicitud | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  // Estados para datos dinámicos (asesores, usuarios y empresas)
  const [asesores, setAsesores] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]); // Todos los usuarios (para Asesor Origen)
  const [empresas, setEmpresas] = useState<Empresa[]>([]); // Todas las empresas (cache)
  const [empresasDelAsesor, setEmpresasDelAsesor] = useState<Empresa[]>([]); // Empresas filtradas por asesor actual
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  // Pre-análisis: ejecución del análisis
  const [isRunningAnalisis, setIsRunningAnalisis] = useState(false);
  const [acceptingPropuesta, setAcceptingPropuesta] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [isTogglingConcluido, setIsTogglingConcluido] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<{ label: string; action: () => Promise<void> } | null>(null);

  // Scroll hacia arriba cuando aparece un error
  useEffect(() => {
    if (error && errorRef.current) {
      // Buscar el contenedor scrollable del modal y hacer scroll hacia arriba
      const scrollContainer = errorRef.current.closest('.overflow-y-auto') || errorRef.current.closest('[role="dialog"]');
      if (scrollContainer) {
        scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
      }
      // También hacer scroll del errorRef al viewport
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [error]);

  // Cargar asesores, todos los usuarios y empresas cuando se abre el modal
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    Promise.all([
      getAsesoresCached(),
      getAllUsersCached(),
      getEmpresasCached()
    ]).then(([asesoresData, allUsersData, empresasData]) => {
      if (cancelled) return;
      setAsesores(asesoresData);
      setAllUsers(allUsersData);
      setEmpresas(empresasData);
    });

    return () => { cancelled = true; };
  }, [isOpen]);

  // Cargar detalle completo cuando se abre el modal
  const loadedIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!isOpen) {
      loadedIdRef.current = null;
      setLocalSolicitud(null);
      setHasChanges(false);
      setIsLoadingDetail(false);
      setError(null); // Limpiar error al cerrar
      return;
    }

    if (!solicitud?.id || isSaving) return;
    // Si ya cargamos este id, no volver a cargar
    if (loadedIdRef.current === solicitud.id) return;

    loadedIdRef.current = solicitud.id;
    setIsLoadingDetail(true);

    solicitudService
      .getById(solicitud.id)
      .then((resp) => {
        if (resp.data) {
          // DEBUG: Verificar comentarios recibidos del backend
          console.log(`[SolicitudDetail] getById response for ${solicitud.id}:`, {
            comentarios: resp.data.comentarios,
            observacionesAnalisis: resp.data.observacionesAnalisis,
          });
          setLocalSolicitud(resp.data);
        } else {
          setLocalSolicitud(solicitud);
        }
        setHasChanges(false);
      })
      .catch(() => {
        setLocalSolicitud(solicitud);
        setHasChanges(false);
      })
      .finally(() => {
        setIsLoadingDetail(false);
      });
  }, [isOpen, solicitud?.id, isSaving]);

  // Cargar empresas del asesor origen (ownerID) cuando cambia localSolicitud
  // Si no hay empresas asignadas al asesor, carga todas las empresas como fallback
  useEffect(() => {
    if (!localSolicitud?.ownerID) {
      setEmpresasDelAsesor([]);
      setLoadingEmpresas(false);
      return;
    }
    let cancelled = false;
    setLoadingEmpresas(true);
    setEmpresasDelAsesor([]); // Limpiar mientras carga para evitar inconsistencias

    empresaService.getEmpresasPorAsesor(localSolicitud.ownerID).then(async (resp) => {
      if (cancelled) return;
      if (resp.data && resp.data.length > 0) {
        const sorted = [...resp.data].sort((a, b) =>
          (a.nombre || '').localeCompare(b.nombre || '', 'es')
        );
        setEmpresasDelAsesor(sorted);
      } else {
        // Fallback: si no hay empresas asignadas al asesor, cargar todas las empresas
        const allResp = await empresaService.getAll();
        if (cancelled) return;
        if (allResp.data && allResp.data.length > 0) {
          const sorted = [...allResp.data].sort((a, b) =>
            (a.nombre || '').localeCompare(b.nombre || '', 'es')
          );
          setEmpresasDelAsesor(sorted);
        } else {
          setEmpresasDelAsesor([]);
        }
      }
    }).catch(async () => {
      if (cancelled) return;
      // En caso de error, también intentar cargar todas las empresas
      try {
        const allResp = await empresaService.getAll();
        if (cancelled) return;
        if (allResp.data && allResp.data.length > 0) {
          const sorted = [...allResp.data].sort((a, b) =>
            (a.nombre || '').localeCompare(b.nombre || '', 'es')
          );
          setEmpresasDelAsesor(sorted);
        } else {
          setEmpresasDelAsesor([]);
        }
      } catch {
        if (!cancelled) setEmpresasDelAsesor([]);
      }
    }).finally(() => {
      if (!cancelled) setLoadingEmpresas(false);
    });

    return () => { cancelled = true; };
  }, [localSolicitud?.ownerID]);

  // Sincronizar voboRequerido, clientePagadora, etc. cuando se cargan las empresas
  // Esto asegura que si la solicitud viene del listado sin estos campos, se llenen de la empresa
  useEffect(() => {
    if (!localSolicitud?.clienteEmpresa || empresasDelAsesor.length === 0) return;

    const emp = empresasDelAsesor.find(e => e.nombre === localSolicitud.clienteEmpresa);
    if (!emp) return;

    // Solo actualizar si los campos están vacíos (no sobrescribir valores existentes)
    const needsUpdate =
      (!localSolicitud.voboRequerido && emp.voboRequerido) ||
      (!localSolicitud.clientePagadora && emp.empresaPagadora) ||
      (!localSolicitud.clienteTipoEmpresa && emp.tipo) ||
      (!localSolicitud.clienteNivelRiesgoEmpresa && emp.nivel);

    if (needsUpdate) {
      setLocalSolicitud(prev => prev ? {
        ...prev,
        voboRequerido: prev.voboRequerido || emp.voboRequerido || '',
        clientePagadora: prev.clientePagadora || emp.empresaPagadora || '',
        clienteTipoEmpresa: prev.clienteTipoEmpresa || emp.tipo || '',
        clienteNivelRiesgoEmpresa: prev.clienteNivelRiesgoEmpresa || emp.nivel || '',
      } : prev);
    }
  }, [empresasDelAsesor, localSolicitud?.clienteEmpresa]);

  /**
   * Handler para cambio de Asesor Final.
   * Actualiza: inheritedID, asesorNombre, regionNombre
   * Limpia: clienteEmpresa (fuerza re-selección de empresa del nuevo asesor)
   */
  const handleAsesorFinalChange = useCallback((newAsesorId: string) => {
    if (!localSolicitud) return;

    const asesor = asesores.find(a => a.ownerID === newAsesorId);

    setLocalSolicitud({
      ...localSolicitud,
      inheritedID: newAsesorId,
      asesorNombre: asesor?.name || asesor?.userName || '',
      regionNombre: asesor?.region || '',
      clienteEmpresa: '', // Forzar re-selección de empresa
      voboRequerido: '', // Limpiar campos dependientes de empresa
      clientePagadora: '',
      clienteTipoEmpresa: '',
      clienteNivelRiesgoEmpresa: '',
    });
    setHasChanges(true);
  }, [localSolicitud, asesores, setLocalSolicitud, setHasChanges]);

  /**
   * Handler para cambio de Asesor Origen.
   * Actualiza: ownerID
   * Limpia: clienteEmpresa, regionNombre (se actualizarán cuando se seleccione empresa)
   */
  const handleAsesorOrigenChange = useCallback((newAsesorId: string) => {
    if (!localSolicitud) return;

    setLocalSolicitud({
      ...localSolicitud,
      ownerID: newAsesorId,
      clienteEmpresa: '', // Forzar re-selección de empresa del nuevo asesor
      regionNombre: '', // Se actualizará cuando se seleccione empresa
      voboRequerido: '', // Limpiar campos dependientes de empresa
      clientePagadora: '',
      clienteTipoEmpresa: '',
      clienteNivelRiesgoEmpresa: '',
    });
    setHasChanges(true);
  }, [localSolicitud, setLocalSolicitud, setHasChanges]);

  // Mostrar loading mientras carga
  if (!solicitud) return null;

  if (isLoadingDetail || !localSolicitud) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Cargando...">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Cargando detalle de la solicitud...</span>
        </div>
      </Modal>
    );
  }

  const formatLegacyDateTime = () => {
    const d = new Date();
    // formato tipo legacy: YYYY-MM-DD HH:mm:ss (sin zona)
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const toggleDateField = async (field: keyof Solicitud, enabled: boolean) => {
    if (!solicitud.id) return;
    setError(null);
    setUpdatingField(String(field));
    try {
      const viewName = modulo === 'revision-documental' ? "Revision Documental"
        : modulo === 'clabes' ? "CLABES"
        : modulo === 'cobranza' ? "Cobranza"
        : modulo === 'vobo' ? "Solicitar VoBo"
        : modulo === 'autorizacion-riesgos' ? "Autorizacion Riesgos"
        : modulo === 'autorizacion-dg' ? "Autorizacion DG"
        : modulo === 'historico' ? "Historico"
        : "Solicitudes";
      const value = enabled ? formatLegacyDateTime() : "";

      // FIX: Antes de activar el switch, guardar cambios pendientes (comentarios, etc.)
      // Esto evita perder comentarios editados al avanzar el proceso
      if (hasChanges && localSolicitud) {
        const pendingPayload: Record<string, unknown> = {
          viewName,
          [String(field)]: value, // Incluir el switch en el mismo payload
        };
        // Incluir campos que podrían haberse editado localmente
        if (localSolicitud.comentarios !== undefined) {
          pendingPayload.comentarios = localSolicitud.comentarios || '';
        }
        if (localSolicitud.observacionesAnalisis !== undefined) {
          pendingPayload.observacionesAnalisis = localSolicitud.observacionesAnalisis || '';
        }
        if (localSolicitud.clienteReferenciaCobranza !== undefined) {
          pendingPayload.clienteReferenciaCobranza = localSolicitud.clienteReferenciaCobranza || '';
        }

        const resp = await solicitudService.updateFull(solicitud.id, pendingPayload as Partial<Solicitud> & { viewName?: string });
        if (resp.data) {
          // Al activar el switch de Autorizado Riesgos, registrar automáticamente quién lo activó
          if (enabled && field === 'fechaPropuesta') {
            const cu = getCurrentUser();
            const userName: string = cu?.userName || '';
            if (userName) {
              solicitudService.updateField(solicitud.id, 'usuarioAutorizacionRiesgos', userName, viewName).catch(() => {});
            }
          }
          if (modulo === 'clabes') {
            setLocalSolicitud(resp.data);
            onUpdate(resp.data);
            setHasChanges(false);
          } else {
            onUpdate(resp.data);
            onClose();
          }
        } else {
          setError(resp.error || "No se pudo actualizar el campo.");
        }
        return;
      }

      // Sin cambios pendientes: usar updateField normal (solo el campo de fecha)
      // Si es fechaValidacionClabe y estamos en módulo clabes, usar endpoint específico
      if (field === 'fechaValidacionClabe' && modulo === 'clabes') {
        const resp = await solicitudService.updateField(solicitud.id, String(field), value, "CLABES");
        if (resp.data) {
          setLocalSolicitud(resp.data);
          onUpdate(resp.data);
          setHasChanges(false);
        } else {
          setError(resp.error || "No se pudo actualizar el campo.");
        }
      } else if (field === 'fechaReferenciaCobranza' && modulo === 'cobranza') {
        // Usar el mismo flujo que otros switches (updateField) para fechaReferenciaCobranza
        const resp = await solicitudService.updateField(solicitud.id, String(field), value, "Cobranza");
        if (resp.data) {
          const r = resp.data as unknown as Record<string, unknown>;
          const fechaVal = (r?.fechaReferenciaCobranza ?? r?.fecha_referencia_cobranza ?? (enabled ? value : undefined)) as string | undefined;
          const updated: Solicitud = { ...localSolicitud, fechaReferenciaCobranza: fechaVal };
          setLocalSolicitud(updated);
          onUpdate(updated);
          setHasChanges(false);
        } else {
          setError(resp.error || "No se pudo actualizar el campo.");
        }
      } else {
        const resp = await solicitudService.updateField(solicitud.id, String(field), value, viewName);
        if (resp.data) {
          // Al activar el switch de Autorizado Riesgos, registrar automáticamente quién lo activó
          if (enabled && field === 'fechaPropuesta') {
            const cu = getCurrentUser();
            const userName: string = cu?.userName || '';
            if (userName) {
              solicitudService.updateField(solicitud.id, 'usuarioAutorizacionRiesgos', userName, viewName).catch(() => {});
            }
          }
          if (modulo === 'clabes') {
            setLocalSolicitud(resp.data);
            onUpdate(resp.data);
            setHasChanges(false);
          } else {
            onUpdate(resp.data);
            onClose();
          }
        } else {
          setError(resp.error || "No se pudo actualizar el campo.");
        }
      }
    } catch (e: any) {
      setError(e?.message || "Error al actualizar el campo.");
    } finally {
      setUpdatingField(null);
    }
  };

  const requestToggle = (field: keyof Solicitud, enabled: boolean): Promise<void> => {
    const label = SWITCH_LABELS[String(field)] || String(field);
    setPendingToggle({ label, action: () => toggleDateField(field, enabled) });
    return Promise.resolve();
  };

  const requestAnalisisConcluido = (checked: boolean): Promise<void> => {
    setPendingToggle({ label: 'Análisis Concluído', action: () => handleAnalisisConcluido(checked) });
    return Promise.resolve();
  };

  // Campos obligatorios para guardar una solicitud (mismos que en creación)
  // Excepciones: clienteApellidoMaterno y clienteClabe son opcionales
  const REQUIRED_FIELDS: { field: keyof Solicitud; label: string }[] = [
    { field: 'inheritedID', label: 'Asesor Final' },
    { field: 'producto', label: 'Producto' },
    { field: 'clienteEmpresa', label: 'Empresa' },
    { field: 'clienteNombre', label: 'Nombre' },
    { field: 'clienteApellidoPaterno', label: 'Apellido Paterno' },
    { field: 'clienteNuevoRenovacion', label: 'Nuevo/Renovación' },
    { field: 'clientePuesto', label: 'Puesto' },
    { field: 'clienteAntiguedad', label: 'Antigüedad' },
    { field: 'clienteNombreJefe', label: 'Nombre de Jefe' },
    { field: 'clienteCorreoJefe', label: 'Correo de Jefe' },
    { field: 'clienteCorreo', label: 'Correo del Cliente' },
    { field: 'clienteSeEnteroPor', label: 'Se enteró por' },
    { field: 'polizaSeguro', label: 'Póliza de Seguro' },
    { field: 'montoSolicitado', label: 'Monto Solicitado' },
    { field: 'plazoSolicitado', label: 'Plazo Solicitado' },
  ];

  const validateRequiredFields = (): string[] => {
    if (!localSolicitud) return ['No hay datos de solicitud'];
    const missing: string[] = [];
    for (const { field, label } of REQUIRED_FIELDS) {
      const value = localSolicitud[field];
      if (value === undefined || value === null || String(value).trim() === '') {
        missing.push(label);
      }
    }
    return missing;
  };

  const handleSave = async () => {
    if (!localSolicitud?.id || !solicitud) return;

    // Validar campos obligatorios solo en módulos donde el formulario los muestra
    if (modulo !== 'vobo') {
      const missingFields = validateRequiredFields();
      if (missingFields.length > 0) {
        setError(`Campos obligatorios faltantes: ${missingFields.join(', ')}`);
        return;
      }
    }

    setError(null);
    setIsSaving(true);
    try {
      // Preparar el objeto a enviar, excluyendo campos calculados y no editables
      const excludedFields = new Set([
        'id', 'diasTranscurridos', 'editable', 'viewName', 'field', 'value', 'userName', 'owner_id'
      ]);

      // Crear payload solo con campos que existen y no están excluidos
      const payload: any = {
        viewName: modulo === 'cobranza' ? 'Cobranza'
          : modulo === 'clabes' ? 'CLABES'
          : modulo === 'pre-analisis' ? 'Pre Analisis'
          : modulo === 'analisis-credito' ? 'Analisis Creditos'
          : modulo === 'vobo' ? 'Solicitar VoBo'
          : modulo === 'autorizacion-riesgos' ? 'Autorizacion Riesgos'
          : modulo === 'autorizacion-dg' ? 'Autorizacion DG'
          : modulo === 'historico' ? 'Historico'
          : 'Solicitudes',
      };

      // Solo incluir campos que realmente cambiaron o que son importantes
      const fieldsToInclude = [
        'comentarios', 'documentos', 'solicitudEstatus', 'solicitudNumeroControl',
        'clienteNombre', 'clienteApellidoPaterno', 'clienteApellidoMaterno',
        'clienteEmpresa', 'producto', 'montoSolicitado', 'montoAutorizado', 'montoTransferencia',
        'montoComision', 'plazoSolicitado', 'plazoAutorizado', 'polizaSeguro',
        'clientePuesto', 'clienteAntiguedad', 'clienteAntiguedadMeses', 'clienteClabe',
        'clienteNombreJefe', 'clienteCorreoJefe', 'clienteCorreo', 'clienteSeEnteroPor',
        'clienteNuevoRenovacion', 'clientePagadora', 'voboRequerido',
        'clienteReferenciaCobranza',
        'inheritedID', 'regionNombre', 'asesorNombre',
        'fechaVencimientoLineaDeCredito', 'autorizacionDG', 'contratoDocumento',
        'motivoNoCierre', 'usuarioAutorizacionRiesgos',
        // Fechas
        'fechaContacto', 'fechaEntregaARiesgos', 'fechaPropuesta', 'fechaAutorizacionDG',
        'fechaContratoImpreso', 'fechaEntregaContratoFirmado', 'fechaFondeado',
        'fechaRechazoRiesgos', 'fechaRechazoDG', 'fechaCancelacionCliente', 'fechaCancelacionCartera',
        'fechaPrimerSolicitudVoBo', 'fechaSegundaSolicitudVoBo', 'fechaTercerSolicitudVoBo', 'fechaVoBo',
        'fechaRevisionDocumentalConluida', 'fechaAnalisisConluido',
        // Pre Análisis
        'clienteDirectivo', 'clienteFechaIngreso', 'frecuenciaDePago',
        'clienteSalario', 'clienteDeduccionesMensuales', 'clienteDeduccionesQuincenales',
        'clienteDeduccionesSemanales', 'tipoListaNegra', 'saldo1a89', 'saldoMayor90',
        'saldoMayor120', 'solicitudMop', 'solicitudEsCleanUp',
        'solicitudSaldoActualSIACAnterior', 'fechaEmisionContrato', 'fechaPrimerCobro',
        'tipoBuroCredito', 'montoCapacidadDePago', 'clienteDeduccionesPeriodo', 'clienteSindicalizado',
        // Analisis Creditos
        'solicitudDestinoCredito', 'clienteTipoEmpresa', 'clienteNivelRiesgoEmpresa',
        'clienteAvalRequerido', 'observacionesAnalisis',
        'excepcionDocumentada1', 'excepcionDocumentada2', 'excepcionDocumentada3',
        'solicitudNumeroControlAnterior', 'solicitudTasaTipoAnterior',
        'solicitudTotalPagadoSIACAnterior', 'solicitudSaldoParcialidadesNumeroAnterior',
        'solicitudSaldoParcialidadesMontoAnterior',
        'tasaAutorizada', 'tasaDeComision',
        'ownerID', 'lineaDeCredito',
      ];

      // Incluir solo campos que están en la lista o que han cambiado
      for (const field of fieldsToInclude) {
        if (field in localSolicitud && !excludedFields.has(field)) {
          const value = (localSolicitud as any)[field];
          // Incluir si tiene valor (0 y false son válidos, solo excluir undefined/null)
          if (value !== undefined && value !== null) {
            payload[field] = value;
          }
        }
      }

      // Asegurar que comentarios siempre esté incluido si existe
      if (localSolicitud.comentarios !== undefined) {
        payload.comentarios = localSolicitud.comentarios || '';
      }
      const resp = await solicitudService.updateFull(localSolicitud.id, payload);
      if (resp.data) {
        // Usar la respuesta del servidor como fuente de verdad
        // Combinar con localSolicitud solo para campos que no vienen en la respuesta
        const updatedData: Solicitud = { ...localSolicitud, ...(resp.data as Partial<Solicitud>) };

        // Asegurarse de que los campos editados estén presentes
        for (const field of Object.keys(payload)) {
          if (field !== 'viewName' && payload[field] !== undefined) {
            (updatedData as unknown as Record<string, unknown>)[field] = payload[field];
          }
        }

        setLocalSolicitud(updatedData);
        onUpdate(updatedData);
        setHasChanges(false);
        setError(null);
        setSuccessMsg('Cambios guardados correctamente.');
        setTimeout(() => setSuccessMsg(null), 4000);
      } else {
        setError(resp.error || "No se pudo guardar los cambios.");
      }
    } catch (e: any) {
      setError(e?.message || "Error al guardar los cambios.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveRevision = async () => {
    if (!localSolicitud?.id) return;
    setError(null);
    setIsSavingRevision(true);
    try {
      const revisionData: Record<string, string> = {};
      for (const { key } of REVISION_DOCUMENTAL_FIELDS) {
        revisionData[key] = getRevisionDocumentalValue(localSolicitud, key);
      }
      const payload: Partial<Solicitud> & { viewName: string } = {
        viewName: 'Revision Documental',
        ...revisionData,
      };
      if (localSolicitud.fechaRevisionDocumentalConluida) {
        payload.fechaRevisionDocumentalConluida = localSolicitud.fechaRevisionDocumentalConluida;
      }
      const payloadForApi: Partial<Solicitud> & { viewName: string } =
        payload as unknown as Partial<Solicitud> & { viewName: string };
      const resp = await solicitudService.updateFull(localSolicitud.id, payloadForApi);
      if (resp.data) {
        setLocalSolicitud(resp.data);
        onUpdate(resp.data);
        setError(null);
        setSuccessMsg('Revisión documental guardada correctamente.');
        setTimeout(() => setSuccessMsg(null), 4000);
      } else {
        setError(resp.error || 'No se pudo guardar la revisión.');
      }
    } catch (e: any) {
      setError(e?.message || 'Error al guardar la revisión.');
    } finally {
      setIsSavingRevision(false);
    }
  };

  const handleRealizarAnalisis = async () => {
    if (!localSolicitud?.id) return;
    const currentUser = getCurrentUser();
    const currentUserOwnerID = currentUser?.ownerID || (currentUser as any)?.sub || '';
    if (!currentUserOwnerID) {
      setError('No se pudo obtener el usuario actual.');
      return;
    }
    if (!localSolicitud.clienteFechaIngreso || !localSolicitud.fechaEmisionContrato || !localSolicitud.fechaPrimerCobro) {
      setError('Complete las fechas requeridas: Fecha de Ingreso, Fecha Emisión Contrato y Fecha Primer Cobro.');
      return;
    }
    setError(null);
    setIsRunningAnalisis(true);
    try {
      // Solo enviamos los datos del formulario; el backend hace todos los cálculos
      // Usar el ownerID original de la solicitud, no el del usuario actual
      const formData = {
        ownerID: localSolicitud.ownerID,
        // Datos de la solicitud
        regionNombre: localSolicitud.regionNombre ?? '',
        clienteEmpresa: localSolicitud.clienteEmpresa ?? '',
        clienteNombre: localSolicitud.clienteNombre ?? '',
        clienteApellidoPaterno: localSolicitud.clienteApellidoPaterno ?? '',
        clienteApellidoMaterno: localSolicitud.clienteApellidoMaterno ?? '',
        producto: localSolicitud.producto ?? '',
        montoSolicitado: localSolicitud.montoSolicitado ?? 0,
        plazoSolicitado: localSolicitud.plazoSolicitado ?? '',
        documentos: localSolicitud.documentos ?? '',
        // Formulario de pre análisis
        polizaSeguro: localSolicitud.polizaSeguro ?? '',
        clienteDirectivo: localSolicitud.clienteDirectivo ?? false,
        clienteSindicalizado: localSolicitud.clienteSindicalizado ?? '',
        clienteFechaIngreso: localSolicitud.clienteFechaIngreso,
        frecuenciaDePago: localSolicitud.frecuenciaDePago ?? '',
        clienteSalario: localSolicitud.clienteSalario ?? 0,
        clienteDeduccionesMensuales: localSolicitud.clienteDeduccionesMensuales ?? 0,
        clienteDeduccionesQuincenales: localSolicitud.clienteDeduccionesQuincenales ?? 0,
        clienteDeduccionesSemanales: localSolicitud.clienteDeduccionesSemanales ?? 0,
        tipoListaNegra: localSolicitud.tipoListaNegra ?? '',
        saldo1a89: localSolicitud.saldo1a89 ?? 0,
        saldoMayor90: localSolicitud.saldoMayor90 ?? 0,
        saldoMayor120: localSolicitud.saldoMayor120 ?? 0,
        solicitudMop: localSolicitud.solicitudMop ?? '',
        solicitudEsCleanUp: localSolicitud.solicitudEsCleanUp ?? false,
        solicitudSaldoActualSIACAnterior: localSolicitud.solicitudSaldoActualSIACAnterior ?? 0,
        solicitudSaldoParcialidadesMontoAnterior: localSolicitud.solicitudSaldoParcialidadesMontoAnterior ?? 0,
        fechaEmisionContrato: localSolicitud.fechaEmisionContrato,
        fechaPrimerCobro: localSolicitud.fechaPrimerCobro,
        // FIX: Incluir comentarios para evitar perderlos al ejecutar análisis
        comentarios: localSolicitud.comentarios ?? '',
      };
      const resp = await solicitudService.realizarAnalisis(String(localSolicitud.id), formData as any);
      if (resp.data) {
        setLocalSolicitud(resp.data);
        onUpdate(resp.data);
        setHasChanges(false);
      } else {
        setError(resp.error || 'No se pudo realizar el análisis.');
      }
    } catch (e: any) {
      setError(e?.message || 'Error al ejecutar el análisis.');
    } finally {
      setIsRunningAnalisis(false);
    }
  };

  const handleRealizarAnalisisCreditos = async () => {
    if (!localSolicitud?.id) return;
    const currentUser = getCurrentUser();
    const currentUserOwnerID = currentUser?.ownerID || (currentUser as any)?.sub || '';
    if (!currentUserOwnerID) {
      setError('No se pudo obtener el usuario actual.');
      return;
    }
    setError(null);
    setIsRunningAnalisis(true);

    // DEBUG: Log de valores de Clean Up en localSolicitud ANTES de crear formData
    console.log('[handleRealizarAnalisisCreditos] localSolicitud Clean Up ANTES:', {
      solicitudSaldoActualSIACAnterior: localSolicitud.solicitudSaldoActualSIACAnterior,
      solicitudSaldoParcialidadesMontoAnterior: localSolicitud.solicitudSaldoParcialidadesMontoAnterior,
    });

    try {
      // Usar el ownerID original de la solicitud, no el del usuario actual
      const formData = {
        ownerID: localSolicitud.ownerID,
        // Sección 12 - Autorización
        montoAutorizado: localSolicitud.montoAutorizado ?? 0,
        montoTransferencia: localSolicitud.montoTransferencia ?? 0,
        tasaAutorizada: localSolicitud.tasaAutorizada ?? 0,
        tasaDeComision: localSolicitud.tasaDeComision ?? 0,
        plazoAutorizado: localSolicitud.plazoAutorizado ?? '',
        // Datos base
        frecuenciaDePago: localSolicitud.frecuenciaDePago ?? '',
        clienteSalario: localSolicitud.clienteSalario ?? 0,
        clienteDeduccionesMensuales: localSolicitud.clienteDeduccionesMensuales ?? 0,
        clienteDeduccionesQuincenales: localSolicitud.clienteDeduccionesQuincenales ?? 0,
        clienteDeduccionesSemanales: localSolicitud.clienteDeduccionesSemanales ?? 0,
        polizaSeguro: localSolicitud.polizaSeguro ?? '',
        producto: localSolicitud.producto ?? '',
        plazoSolicitado: localSolicitud.plazoSolicitado ?? '',
        montoSolicitado: localSolicitud.montoSolicitado ?? 0,
        clienteSindicalizado: localSolicitud.clienteSindicalizado ?? '',
        // Sección 8 - Clean Up
        solicitudEsCleanUp: localSolicitud.solicitudEsCleanUp ?? false,
        solicitudNumeroControlAnterior: localSolicitud.solicitudNumeroControlAnterior ?? '',
        solicitudTasaTipoAnterior: localSolicitud.solicitudTasaTipoAnterior ?? '',
        solicitudSaldoActualSIACAnterior: localSolicitud.solicitudSaldoActualSIACAnterior ?? 0,
        solicitudTotalPagadoSIACAnterior: localSolicitud.solicitudTotalPagadoSIACAnterior ?? 0,
        solicitudSaldoParcialidadesNumeroAnterior: localSolicitud.solicitudSaldoParcialidadesNumeroAnterior ?? 0,
        solicitudSaldoParcialidadesMontoAnterior: localSolicitud.solicitudSaldoParcialidadesMontoAnterior ?? 0,
        // Sección 9 - Línea de Crédito
        fechaVencimientoLineaDeCredito: localSolicitud.fechaVencimientoLineaDeCredito ?? '',
        // Sección 11 - Excepciones
        excepcionDocumentada1: localSolicitud.excepcionDocumentada1 ?? '',
        excepcionDocumentada2: localSolicitud.excepcionDocumentada2 ?? '',
        excepcionDocumentada3: localSolicitud.excepcionDocumentada3 ?? '',
        // Sección 13 - Condiciones del Contrato
        solicitudNumeroControl: localSolicitud.solicitudNumeroControl ?? '',
        fechaEmisionContrato: localSolicitud.fechaEmisionContrato ?? '',
        fechaPrimerCobro: localSolicitud.fechaPrimerCobro ?? '',
        // Observaciones
        observacionesAnalisis: localSolicitud.observacionesAnalisis ?? '',
        comentarios: localSolicitud.comentarios ?? '',
      };
      const resp = await solicitudService.realizarAnalisisCreditos(String(localSolicitud.id), formData as any);
      if (resp.data) {
        setLocalSolicitud(resp.data);
        onUpdate(resp.data);
        setHasChanges(false);
        setSuccessMsg('Analisis de creditos ejecutado correctamente.');
        setTimeout(() => setSuccessMsg(null), 5000);
      } else {
        setError(resp.error || 'No se pudo ejecutar el analisis de creditos.');
      }
    } catch (e: any) {
      setError(e?.message || 'Error al ejecutar el analisis de creditos.');
    } finally {
      setIsRunningAnalisis(false);
    }
  };

  const handleAnalisisConcluido = async (checked: boolean) => {
    if (!localSolicitud?.id) return;
    setError(null);
    setIsTogglingConcluido(true);
    try {
      const formatLegacy = () => {
        const d = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      };

      const fechaValue = checked ? formatLegacy() : '';

      const payload: any = {
        viewName: 'Analisis Creditos',
        fechaAnalisisConluido: fechaValue,
      };

      // FIX: Incluir comentarios y observaciones si hay cambios pendientes
      // Esto evita perder comentarios editados al activar el switch
      if (hasChanges) {
        if (localSolicitud.comentarios !== undefined) {
          payload.comentarios = localSolicitud.comentarios || '';
        }
        if (localSolicitud.observacionesAnalisis !== undefined) {
          payload.observacionesAnalisis = localSolicitud.observacionesAnalisis || '';
        }
      }

      const resp = await solicitudService.updateFull(localSolicitud.id, payload);
      if (resp.data) {
        const updatedData: Solicitud = { ...localSolicitud, ...(resp.data as Partial<Solicitud>), fechaAnalisisConluido: fechaValue };
        setLocalSolicitud(updatedData);
        onUpdate(updatedData);
        setHasChanges(false);
        if (checked) {
          setSuccessMsg('Análisis concluido correctamente.');
        } else {
          setSuccessMsg('Análisis concluido desactivado.');
        }
        setTimeout(() => setSuccessMsg(null), 5000);
      } else {
        setError(resp.error || 'No se pudo actualizar el análisis concluido.');
      }
    } catch (e: any) {
      setError(e?.message || 'Error al actualizar el análisis concluido.');
    } finally {
      setIsTogglingConcluido(false);
    }
  };

  const handleSaveAnalisisForm = async () => {
    if (!localSolicitud?.id) return;

    // Validar campos obligatorios
    const missingFields = validateRequiredFields();
    if (missingFields.length > 0) {
      setError(`Campos obligatorios faltantes: ${missingFields.join(', ')}`);
      return;
    }

    setError(null);
    setIsSaving(true);
    try {
      const payload: any = {
        viewName: 'Analisis Creditos',
      };

      // Calcular errorTransferencia antes de guardar (replica lógica del legacy)
      const mTrans = Number(localSolicitud.montoTransferencia || 0);
      const mAut = Number(localSolicitud.montoAutorizado || 0);
      const mSol = Number(localSolicitud.montoSolicitado || 0);
      const producto = localSolicitud.producto || '';
      const esCleanUp = localSolicitud.solicitudEsCleanUp === true;
      let calculatedError = '';

      if (producto === 'Adelanto de Nómina') {
        if (mTrans !== mAut) {
          calculatedError = 'Transferencia debe ser igual que Autorizado';
        }
      } else if (producto === 'Nómina') {
        if (esCleanUp) {
          if (mAut > mSol) {
            calculatedError = 'No se puede autorizar un monto mayor al solicitado.';
          }
          if (mTrans >= mAut) {
            calculatedError = 'Transferencia debe ser menor que Autorizado.';
          }
        } else {
          if (mTrans !== mAut) {
            calculatedError = 'Transferencia debe ser igual que Autorizado';
          }
          if (mTrans > mAut) {
            calculatedError = 'Transferencia no debe ser mayor que Autorizado';
          }
        }
      }

      const fieldsToSave = [
        // 1. Asesor / Region
        'inheritedID', 'ownerID', 'asesorNombre', 'regionNombre',
        // 2. Datos de la Empresa
        'clienteEmpresa', 'voboRequerido', 'clienteTipoEmpresa', 'clienteNivelRiesgoEmpresa', 'clientePagadora',
        // 3. Datos del Cliente
        'clienteNombre', 'clienteApellidoPaterno', 'clienteApellidoMaterno',
        'clienteNuevoRenovacion', 'clientePuesto', 'clienteDirectivo', 'clienteSindicalizado',
        'clienteFechaIngreso', 'clienteAntiguedadMeses', 'clienteClabe',
        // 4. Ingresos y Deducciones
        'frecuenciaDePago', 'clienteSalario', 'clienteDeduccionesSemanales',
        'clienteDeduccionesQuincenales', 'clienteDeduccionesMensuales', 'clienteDeduccionesPeriodo',
        // 5. Datos de la Solicitud y Seguro
        'producto', 'montoSolicitado', 'solicitudDestinoCredito', 'plazoSolicitado', 'polizaSeguro',
        // 6. Quien es Quien
        'tipoListaNegra',
        // 7. Buro de Credito
        'saldo1a89', 'saldoMayor90', 'saldoMayor120', 'solicitudMop',
        // 8. Clean Up
        'solicitudEsCleanUp', 'solicitudNumeroControlAnterior', 'solicitudTasaTipoAnterior',
        'solicitudSaldoActualSIACAnterior', 'solicitudTotalPagadoSIACAnterior',
        'solicitudSaldoParcialidadesNumeroAnterior', 'solicitudSaldoParcialidadesMontoAnterior',
        // 9. Linea de Credito
        'lineaDeCredito', 'fechaVencimientoLineaDeCredito',
        // 11. Excepciones
        'excepcionDocumentada1', 'excepcionDocumentada2', 'excepcionDocumentada3',
        // 12. Autorizacion
        'plazoAutorizado', 'montoAutorizado', 'montoTransferencia', 'tasaDeComision', 'tasaAutorizada',
        // 14. errorTransferencia se calcula y agrega manualmente arriba
        // 13. Condiciones del Contrato
        'solicitudNumeroControl', 'fechaEmisionContrato', 'fechaPrimerCobro',
        // Observaciones
        'observacionesAnalisis', 'comentarios', 'documentos',
      ];

      // Campos numéricos que deben enviarse incluso cuando son 0 o null (para permitir borrado)
      const numericFieldsAllowEmpty = [
        'solicitudSaldoActualSIACAnterior',
        'solicitudTotalPagadoSIACAnterior',
        'solicitudSaldoParcialidadesNumeroAnterior',
        'solicitudSaldoParcialidadesMontoAnterior',
      ];

      // Campos string que deben enviarse siempre (incluso si son vacíos)
      const stringFieldsAlwaysSend = [
        'errorTransferencia',
      ];

      for (const field of fieldsToSave) {
        if (field in localSolicitud) {
          const value = (localSolicitud as any)[field];
          // Para campos Clean Up numéricos, siempre enviar (incluso si es 0, null, undefined)
          if (numericFieldsAllowEmpty.includes(field)) {
            payload[field] = value ?? 0; // Si es null/undefined, enviar 0
          } else if (stringFieldsAlwaysSend.includes(field)) {
            // Para campos string especiales, siempre enviar (incluso si es vacío)
            payload[field] = value ?? '';
          } else if (value !== undefined && value !== null) {
            payload[field] = value;
          }
        }
      }

      // Siempre incluir errorTransferencia calculado (no depender de localSolicitud)
      payload.errorTransferencia = calculatedError;

      // DEBUG: Log de campos importantes que se van a guardar
      console.log('[handleSaveAnalisisForm] errorTransferencia calculado:', calculatedError);
      console.log('[handleSaveAnalisisForm] errorTransferencia en payload:', payload.errorTransferencia);
      console.log('[handleSaveAnalisisForm] Campos Clean Up en payload:', {
        solicitudSaldoActualSIACAnterior: payload.solicitudSaldoActualSIACAnterior,
        solicitudTotalPagadoSIACAnterior: payload.solicitudTotalPagadoSIACAnterior,
        solicitudSaldoParcialidadesNumeroAnterior: payload.solicitudSaldoParcialidadesNumeroAnterior,
        solicitudSaldoParcialidadesMontoAnterior: payload.solicitudSaldoParcialidadesMontoAnterior,
      });
      console.log('[handleSaveAnalisisForm] Campos Clean Up en localSolicitud ANTES de guardar:', {
        solicitudSaldoActualSIACAnterior: localSolicitud.solicitudSaldoActualSIACAnterior,
        solicitudSaldoParcialidadesMontoAnterior: localSolicitud.solicitudSaldoParcialidadesMontoAnterior,
      });

      if (localSolicitud.comentarios !== undefined) {
        payload.comentarios = localSolicitud.comentarios || '';
      }

      const resp = await solicitudService.updateFull(localSolicitud.id, payload);
      if (resp.data) {
        // DEBUG: Log de respuesta del backend
        console.log('[handleSaveAnalisisForm] errorTransferencia en respuesta:', (resp.data as any).errorTransferencia);
        console.log('[handleSaveAnalisisForm] Respuesta del backend - Campos Clean Up:', {
          solicitudSaldoActualSIACAnterior: (resp.data as any).solicitudSaldoActualSIACAnterior,
          solicitudSaldoParcialidadesMontoAnterior: (resp.data as any).solicitudSaldoParcialidadesMontoAnterior,
        });
        // Preservar el errorTransferencia calculado (el backend puede no devolverlo)
        const updatedData: Solicitud = {
          ...localSolicitud,
          ...(resp.data as Partial<Solicitud>),
          errorTransferencia: calculatedError,  // Siempre usar el error calculado
        };
        console.log('[handleSaveAnalisisForm] updatedData DESPUÉS de merge - Campos Clean Up:', {
          solicitudSaldoActualSIACAnterior: updatedData.solicitudSaldoActualSIACAnterior,
          solicitudSaldoParcialidadesMontoAnterior: updatedData.solicitudSaldoParcialidadesMontoAnterior,
        });
        console.log('[handleSaveAnalisisForm] errorTransferencia final en updatedData:', updatedData.errorTransferencia);
        setLocalSolicitud(updatedData);
        onUpdate(updatedData);
        setHasChanges(false);
        setSuccessMsg('Datos del formulario guardados correctamente.');
        setTimeout(() => setSuccessMsg(null), 5000);
      } else {
        setError(resp.error || 'No se pudo guardar los datos del formulario.');
      }
    } catch (e: any) {
      setError(e?.message || 'Error al guardar los datos del formulario.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSimularSIAC = async () => {
    if (!localSolicitud?.id) return;
    const currentUser = getCurrentUser();
    const currentUserOwnerID = currentUser?.ownerID || (currentUser as any)?.sub || '';
    if (!currentUserOwnerID) {
      setError('No se pudo obtener el usuario actual.');
      return;
    }
    setError(null);
    setIsRunningAnalisis(true);
    try {
      // Usar el ownerID original de la solicitud, no el del usuario actual
      const formData = {
        ownerID: localSolicitud.ownerID,
        montoAutorizado: localSolicitud.montoAutorizado ?? 0,
        montoTransferencia: localSolicitud.montoTransferencia ?? 0,
        tasaAutorizada: localSolicitud.tasaAutorizada ?? 0,
        tasaDeComision: localSolicitud.tasaDeComision ?? 0,
        plazoAutorizado: localSolicitud.plazoAutorizado ?? '',
        frecuenciaDePago: localSolicitud.frecuenciaDePago ?? '',
        clienteSalario: localSolicitud.clienteSalario ?? 0,
        clienteDeduccionesMensuales: localSolicitud.clienteDeduccionesMensuales ?? 0,
        clienteDeduccionesQuincenales: localSolicitud.clienteDeduccionesQuincenales ?? 0,
        clienteDeduccionesSemanales: localSolicitud.clienteDeduccionesSemanales ?? 0,
        polizaSeguro: localSolicitud.polizaSeguro ?? '',
        producto: localSolicitud.producto ?? '',
        plazoSolicitado: localSolicitud.plazoSolicitado ?? '',
        montoSolicitado: localSolicitud.montoSolicitado ?? 0,
        solicitudEsCleanUp: localSolicitud.solicitudEsCleanUp ?? false,
        solicitudSaldoActualSIACAnterior: localSolicitud.solicitudSaldoActualSIACAnterior ?? 0,
        solicitudNumeroControl: localSolicitud.solicitudNumeroControl ?? '',
        fechaEmisionContrato: localSolicitud.fechaEmisionContrato ?? '',
        fechaPrimerCobro: localSolicitud.fechaPrimerCobro ?? '',
        fechaUltimoPago: localSolicitud.fechaUltimoPago ?? '',
        // FIX: Incluir comentarios para evitar perderlos al simular SIAC
        comentarios: localSolicitud.comentarios ?? '',
      };
      const resp = await solicitudService.simularSIAC(String(localSolicitud.id), formData as any);
      if (resp.data) {
        setLocalSolicitud(resp.data);
        onUpdate(resp.data);
        setHasChanges(false);
        setSuccessMsg('Simulación SIAC ejecutada correctamente.');
        setTimeout(() => setSuccessMsg(null), 5000);
      } else {
        setError(resp.error || 'No se pudo ejecutar la simulación SIAC.');
      }
    } catch (e: any) {
      setError(e?.message || 'Error al ejecutar la simulación SIAC.');
    } finally {
      setIsRunningAnalisis(false);
    }
  };

  const handleAceptarPropuesta = async (tipoPropuesta: string) => {
    if (!localSolicitud?.id) return;
    const currentUser = getCurrentUser();
    const ownerID = currentUser?.ownerID || (currentUser as any)?.sub || '';
    if (!ownerID) {
      setError('No se pudo obtener el usuario actual.');
      return;
    }
    setError(null);
    setSuccessMsg(null);
    setAcceptingPropuesta(tipoPropuesta);
    try {
      const resp = await solicitudService.aceptarPropuesta(String(localSolicitud.id), tipoPropuesta, ownerID);
      if (resp.data) {
        setLocalSolicitud(resp.data);
        onUpdate(resp.data);
        setHasChanges(false);
        const nombres: Record<string, string> = {
          Solicitud: 'Condiciones Solicitadas',
          BuenBuro: 'Oferta por Buen Buró',
          Recomendacion: 'Mejor Propuesta',
        };
        setSuccessMsg(`Propuesta "${nombres[tipoPropuesta] || tipoPropuesta}" aceptada y guardada correctamente.`);
        setTimeout(() => setSuccessMsg(null), 5000);
      } else {
        setError(resp.error || 'No se pudo aceptar la propuesta.');
      }
    } catch (e: any) {
      setError(e?.message || 'Error al aceptar la propuesta.');
    } finally {
      setAcceptingPropuesta(null);
    }
  };

  // Solo mostrar datos calculados si ya se ejecutó el análisis (tipoBuroCredito solo se asigna en realizar_analisis)
  const hasDatosCalculados = modulo === 'pre-analisis' && !!localSolicitud.tipoBuroCredito;

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      
      title={
        readOnly
          ? `Detalle de Solicitud #${solicitud.solicitudNumeroControl || solicitud.id} (Solo lectura)`
          : modulo === 'revision-documental'
            ? `Revisión Documental — #${solicitud.solicitudNumeroControl || solicitud.id}`
            : `Solicitud #${solicitud.solicitudNumeroControl || solicitud.id}`
      }
      footer={
        readOnly || modulo === 'autorizacion-dg'
          ? undefined
          : hasChanges && (
              <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                <Button variant="secondary" onClick={onClose} disabled={isSaving}>
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </div>
            )
      }
    >
      <div className="space-y-6" ref={errorRef}>
        {/* Mensaje de error global visible al inicio */}
        {error && (
          <div className="p-4 rounded-lg border-2 border-red-300 bg-red-50 text-red-800 shadow-sm">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="font-semibold text-red-900">Error</p>
                <p className="text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {modulo === 'revision-documental' ? (
          <>
            {/* Formulario completo como en Solicitudes */}
            <SolicitudControlFechas
              localSolicitud={localSolicitud}
              setLocalSolicitud={setLocalSolicitud}
              setHasChanges={setHasChanges}
              error={null}
              asesores={asesores}
              allUsers={allUsers}
              empresas={empresasDelAsesor}
              loadingEmpresas={loadingEmpresas}
              onAsesorFinalChange={handleAsesorFinalChange}
              toggleDateField={requestToggle}
              updatingField={updatingField}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              modulo="revision-documental"
            />
            {/* Sección de Revisión Documental */}
            <RevisionDocumentalSection
              localSolicitud={localSolicitud}
              setLocalSolicitud={setLocalSolicitud}
              setHasChanges={setHasChanges}
              error={null}
              successMsg={successMsg}
              isSavingRevision={isSavingRevision}
              handleSaveRevision={handleSaveRevision}
              toggleDateField={requestToggle}
              hideClientData={true}
            />
          </>
        ) : (
          <>
            {modulo === 'pre-analisis' && localSolicitud && (
              <PreAnalisisSection
                localSolicitud={localSolicitud}
                setLocalSolicitud={setLocalSolicitud}
                setHasChanges={setHasChanges}
                error={null}
                empresas={empresas}
                isRunningAnalisis={isRunningAnalisis}
                handleRealizarAnalisis={handleRealizarAnalisis}
                handleAceptarPropuesta={handleAceptarPropuesta}
                hasDatosCalculados={hasDatosCalculados}
                acceptingPropuesta={acceptingPropuesta}
                successMsg={successMsg}
              />
            )}

            {modulo === 'analisis-credito' && (
              <AnalisisCreditosSection
                localSolicitud={localSolicitud}
                setLocalSolicitud={setLocalSolicitud}
                setHasChanges={setHasChanges}
                error={null}
                asesores={asesores}
                allUsers={allUsers}
                empresas={empresasDelAsesor}
                loadingEmpresas={loadingEmpresas}
                onAsesorFinalChange={handleAsesorFinalChange}
                isRunningAnalisis={isRunningAnalisis}
                isSaving={isSaving}
                handleRealizarAnalisisCreditos={handleRealizarAnalisisCreditos}
                handleSimularSIAC={handleSimularSIAC}
                handleSaveForm={handleSaveAnalisisForm}
                handleAnalisisConcluido={requestAnalisisConcluido}
                isTogglingConcluido={isTogglingConcluido}
                successMsg={successMsg}
              />
            )}

            {modulo === 'vobo' && (
              <>
                {successMsg && (
                  <div className="p-3 rounded border border-green-200 bg-green-50 text-green-700 text-sm font-medium">
                    {successMsg}
                  </div>
                )}
                <SolicitarVoBoSection
                  localSolicitud={localSolicitud}
                  setLocalSolicitud={setLocalSolicitud}
                  setHasChanges={setHasChanges}
                  asesores={asesores}
                  empresas={empresasDelAsesor}
                  loadingEmpresas={loadingEmpresas}
                  onAsesorFinalChange={handleAsesorFinalChange}
                  toggleDateField={requestToggle}
                  updatingField={updatingField}
                />
              </>
            )}

            {modulo === 'autorizacion-riesgos' && (
              <AutorizacionRiesgosSection
                localSolicitud={localSolicitud}
                setLocalSolicitud={setLocalSolicitud}
                setHasChanges={setHasChanges}
                asesores={asesores}
                allUsers={allUsers}
                empresas={empresasDelAsesor}
                loadingEmpresas={loadingEmpresas}
                onAsesorFinalChange={handleAsesorFinalChange}
                onAsesorOrigenChange={handleAsesorOrigenChange}
                toggleDateField={requestToggle}
                updatingField={updatingField}
              />
            )}

            {modulo === 'cobranza' && (
              <CobranzaSection
                localSolicitud={localSolicitud}
                setLocalSolicitud={setLocalSolicitud}
                setHasChanges={setHasChanges}
                toggleDateField={requestToggle}
                updatingField={updatingField}
                asesores={asesores}
                error={null}
              />
            )}

            {modulo === 'clabes' && (
              <ClabesSection
                localSolicitud={localSolicitud}
                setLocalSolicitud={setLocalSolicitud}
                setHasChanges={setHasChanges}
                toggleDateField={requestToggle}
                updatingField={updatingField}
                asesores={asesores}
                error={null}
              />
            )}

            {modulo !== 'pre-analisis' && modulo !== 'analisis-credito' && modulo !== 'vobo' && modulo !== 'autorizacion-riesgos' && modulo !== 'cobranza' && modulo !== 'clabes' && (
              <SolicitudControlFechas
                localSolicitud={localSolicitud}
                setLocalSolicitud={setLocalSolicitud}
                setHasChanges={setHasChanges}
                error={null}
                asesores={asesores}
                allUsers={allUsers}
                empresas={empresasDelAsesor}
                loadingEmpresas={loadingEmpresas}
                onAsesorFinalChange={handleAsesorFinalChange}
                toggleDateField={requestToggle}
                updatingField={updatingField}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                readOnly={readOnly}
                modulo={modulo}
                {...((modulo === 'imprimir-contrato' || modulo === 'firmar-contrato' || modulo === 'historico') ? {
                  onSimularSIAC: handleSimularSIAC,
                  isSimulating: isRunningAnalisis,
                } : {})}
              />
            )}

            {/* Link de Documentos en Histórico - visible para todos los usuarios */}
            {modulo === 'historico' && localSolicitud?.documentos && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-800">Documentos de la solicitud</span>
                  <Button
                    variant="primary"
                    onClick={() => window.open(localSolicitud.documentos, '_blank')}
                  >
                    Link Documentos
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>

    {/* Confirmación de acción de switch */}
    {pendingToggle && (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirmar acción</h3>
          <p className="text-sm text-gray-600 mb-6">
            ¿Confirmas que deseas modificar el campo{' '}
            <strong className="text-gray-900">{pendingToggle.label}</strong>?
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setPendingToggle(null)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                const action = pendingToggle.action;
                setPendingToggle(null);
                await action();
              }}
            >
              Confirmar
            </Button>
          </div>
        </div>
      </div>
    )}
  </>
  );
};
